import {
  addDays,
  PURCHASE_ORDER_DUE_WINDOW_DAYS,
  PURCHASE_ORDER_OPEN_STATUS_IDS,
  todayBusinessDate,
  toPrismaDate,
} from '@nudool/shared';

import type { PurchaseOrderWhere } from '../../generated/graphql/graphql.js';
import {
  parseListRouteSearch,
  type ListRouteSearch,
} from '../../hooks/use-list-table-helpers/index.js';
import { uuidPattern } from '../../lib/uuid-pattern.js';
import { PURCHASE_ORDER_QUEUE_ADMITTED_STATUSES } from './purchase-order-queue-admitted-statuses.js';
import {
  PURCHASE_ORDER_WORKSPACE_QUEUES,
  type PurchaseOrderWorkspaceQueue,
} from './purchase-order-workspace-queues.js';

const MAX_RECENT_IDS = 50;

const VALID_QUEUE_IDS = new Set<string>(PURCHASE_ORDER_WORKSPACE_QUEUES.map((item) => item.id));

export const OPEN_PURCHASE_ORDER_STATUSES = PURCHASE_ORDER_OPEN_STATUS_IDS;

/**
 * UTC midnight of the Pacific business day `days` after `now`'s, the shape a picked expected day
 * is stored in. `purchaseOrderMetricsQuery` compares the stored day with the same Pacific day, so
 * the list's late / due-this-week rows match the metrics rail's counts at any hour.
 */
function utcMidnightOfBusinessDay(now: Date, days = 0): string {
  return toPrismaDate(addDays(todayBusinessDate(now), days)).toISOString();
}

export function parsePurchaseOrderIdsParam(ids?: string): string[] {
  if (!ids?.trim()) return [];
  return ids
    .split(',')
    .map((id) => id.trim())
    .filter((id) => Boolean(id) && uuidPattern.test(id))
    .slice(0, MAX_RECENT_IDS);
}

export function parseWorkspaceQueueParam(value: unknown): PurchaseOrderWorkspaceQueue {
  if (typeof value === 'string' && VALID_QUEUE_IDS.has(value)) {
    return value as PurchaseOrderWorkspaceQueue;
  }
  return 'all';
}

type PurchaseOrderWorkspaceFilterSearch = {
  queue: PurchaseOrderWorkspaceQueue;
  ids?: string;
  vendorId?: string;
};

export function parsePurchaseOrderWorkspaceSearch(
  search: Record<string, unknown>,
): PurchaseOrderWorkspaceFilterSearch & ListRouteSearch {
  const parsed: PurchaseOrderWorkspaceFilterSearch = {
    queue: parseWorkspaceQueueParam(search.queue),
    // Set even when rejected: the router spreads the raw URL search under this result.
    ids: undefined,
    vendorId: undefined,
  };

  if (typeof search.ids === 'string' && search.ids.trim().length > 0) {
    parsed.ids = search.ids.trim();
  }

  if (typeof search.vendorId === 'string' && uuidPattern.test(search.vendorId.trim())) {
    parsed.vendorId = search.vendorId.trim();
  }

  return { ...parsed, ...parseListRouteSearch(search) };
}

export type PurchaseOrderQueueWhere = {
  where?: PurchaseOrderWhere;
  /** Mirrors the `has_open_lines` gate metrics apply to late / dueThisWeek — see settled-decisions.md. */
  hasOpenLines?: boolean;
};

export function buildPurchaseOrderQueueWhere(
  queue: PurchaseOrderWorkspaceQueue,
  now: Date,
): PurchaseOrderQueueWhere {
  switch (queue) {
    case 'late':
      return {
        where: {
          statusId: { in: [...PURCHASE_ORDER_QUEUE_ADMITTED_STATUSES.late] },
          dateFirstShip: { lt: utcMidnightOfBusinessDay(now) },
        },
        hasOpenLines: true,
      };
    case 'dueThisWeek':
      return {
        where: {
          statusId: { in: [...PURCHASE_ORDER_QUEUE_ADMITTED_STATUSES.dueThisWeek] },
          dateFirstShip: {
            gte: utcMidnightOfBusinessDay(now),
            lt: utcMidnightOfBusinessDay(now, PURCHASE_ORDER_DUE_WINDOW_DAYS),
          },
        },
        hasOpenLines: true,
      };
    case 'all':
      return {};
  }
}

export function buildPurchaseOrderListWhere(args: {
  queue: PurchaseOrderWorkspaceQueue;
  recentIds: string[];
  vendorId?: string | null;
  now?: Date;
}): PurchaseOrderQueueWhere {
  const { queue, recentIds, vendorId, now = new Date() } = args;

  if (recentIds.length > 0) {
    return { where: { id: { in: recentIds } } };
  }

  const where: PurchaseOrderWhere = {};

  const queueConstraint = buildPurchaseOrderQueueWhere(queue, now);
  if (queueConstraint.where) {
    Object.assign(where, queueConstraint.where);
  }

  if (vendorId != null && vendorId !== '') {
    where.vendorId = { equals: vendorId };
  }

  return {
    where: Object.keys(where).length === 0 ? undefined : where,
    hasOpenLines: queueConstraint.hasOpenLines,
  };
}
